export const CHICHESTER = {
  latitude: 50.8365,
  longitude: -0.7792,
  label: 'Chichester',
};

export type SiteForecast = {
  label: string;
  postcode?: string | null;
  dayKey: string;
  temperature: number | null;
  high: number | null;
  low: number | null;
  code: number | null;
  summary: string;
  icon: string;
};

export type ChichesterForecast = SiteForecast;

const GEO_CACHE_KEY = 'jarvis_postcode_geo_v1';
const WEATHER_CACHE_KEY = 'jarvis_site_weather_v1';

const UK_POSTCODE = /([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})/gi;

export function extractUkPostcode(address?: string | null): string | null {
  const matches = String(address || '').match(UK_POSTCODE);
  if (!matches || matches.length === 0) return null;
  return String(matches[matches.length - 1] || '').toUpperCase().replace(/\s+/g, ' ').trim() || null;
}

export function weatherFromCode(code?: number | null): { summary: string; icon: string } {
  const n = Number(code);
  if (!Number.isFinite(n)) return { summary: 'Forecast unavailable', icon: '•' };
  if (n === 0) return { summary: 'Clear', icon: '☀️' };
  if (n <= 3) return { summary: 'Partly cloudy', icon: '⛅' };
  if (n <= 48) return { summary: 'Fog', icon: '🌫️' };
  if (n <= 57) return { summary: 'Drizzle', icon: '🌦️' };
  if (n <= 67) return { summary: 'Rain', icon: '🌧️' };
  if (n <= 77) return { summary: 'Snow', icon: '❄️' };
  if (n <= 82) return { summary: 'Showers', icon: '🌧️' };
  if (n <= 86) return { summary: 'Snow showers', icon: '❄️' };
  return { summary: 'Thunderstorms', icon: '⛈️' };
}

function asNumber(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n) : null;
}

export function londonWeatherDayKey(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  const day = parts.find((part) => part.type === 'day')?.value;
  return year && month && day ? `${year}-${month}-${day}` : '';
}

export function forecastFromOpenMeteo(
  payload: unknown,
  dayKey: string,
  label = CHICHESTER.label,
  postcode?: string | null,
): SiteForecast | null {
  if (!payload || typeof payload !== 'object') return null;
  const row = payload as {
    current?: { temperature_2m?: number; weather_code?: number };
    daily?: {
      weather_code?: number[];
      temperature_2m_max?: number[];
      temperature_2m_min?: number[];
    };
  };
  const code = asNumber(row.current?.weather_code ?? row.daily?.weather_code?.[0]);
  const look = weatherFromCode(code);
  return {
    label,
    postcode: postcode || null,
    dayKey,
    temperature: asNumber(row.current?.temperature_2m),
    high: asNumber(row.daily?.temperature_2m_max?.[0]),
    low: asNumber(row.daily?.temperature_2m_min?.[0]),
    code,
    summary: look.summary,
    icon: look.icon,
  };
}

export function formatForecastLine(forecast: SiteForecast): string {
  const now = forecast.temperature != null ? `${forecast.temperature}°` : null;
  const range = forecast.high != null && forecast.low != null
    ? `${forecast.low}–${forecast.high}°`
    : null;
  return [forecast.label, now, forecast.summary, range].filter(Boolean).join(' · ');
}

export type JobWeatherTarget = {
  postcode: string;
  labels: string[];
};

export function jobWeatherTargetsForDay(
  jobs: Array<{ postcode?: string | null; job_title?: string | null; customer_name?: string | null }>,
): JobWeatherTarget[] {
  const byPostcode = new Map<string, Set<string>>();
  jobs.forEach((job) => {
    const postcode = extractUkPostcode(job.postcode) || String(job.postcode || '').trim().toUpperCase();
    if (!postcode || !/^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/.test(postcode)) return;
    const labels = byPostcode.get(postcode) || new Set<string>();
    const label = String(job.customer_name || job.job_title || '').trim();
    if (label) labels.add(label);
    byPostcode.set(postcode, labels);
  });
  return Array.from(byPostcode.entries()).map(([postcode, labels]) => ({
    postcode,
    labels: Array.from(labels),
  }));
}

function readJson<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) as T : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
}

async function geocodePostcode(postcode: string): Promise<{ latitude: number; longitude: number } | null> {
  const compact = postcode.replace(/\s+/g, '').toUpperCase();
  const geoCache = readJson<Record<string, { latitude: number; longitude: number }>>(GEO_CACHE_KEY) || {};
  if (geoCache[compact]) return geoCache[compact];
  const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(compact)}`);
  if (!res.ok) return null;
  const body = await res.json() as { result?: { latitude?: number; longitude?: number } };
  const latitude = Number(body.result?.latitude);
  const longitude = Number(body.result?.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  const next = { latitude, longitude };
  writeJson(GEO_CACHE_KEY, { ...geoCache, [compact]: next });
  return next;
}

async function fetchForecastForCoords(args: {
  latitude: number;
  longitude: number;
  label: string;
  postcode?: string | null;
  dayKey: string;
}): Promise<SiteForecast | null> {
  const cacheKey = `${args.dayKey}:${args.latitude.toFixed(2)},${args.longitude.toFixed(2)}`;
  const weatherCache = readJson<Record<string, SiteForecast>>(WEATHER_CACHE_KEY) || {};
  if (weatherCache[cacheKey]) {
    return { ...weatherCache[cacheKey], label: args.label, postcode: args.postcode || weatherCache[cacheKey].postcode };
  }
  const url = new URL('https://api.open-meteo.com/v1/forecast');
  url.searchParams.set('latitude', String(args.latitude));
  url.searchParams.set('longitude', String(args.longitude));
  url.searchParams.set('current', 'temperature_2m,weather_code');
  url.searchParams.set('daily', 'weather_code,temperature_2m_max,temperature_2m_min');
  url.searchParams.set('timezone', 'Europe/London');
  url.searchParams.set('forecast_days', '1');
  const res = await fetch(url.toString());
  if (!res.ok) return null;
  const forecast = forecastFromOpenMeteo(await res.json(), args.dayKey, args.label, args.postcode);
  if (forecast) writeJson(WEATHER_CACHE_KEY, { ...weatherCache, [cacheKey]: forecast });
  return forecast;
}

export async function fetchChichesterForecast(now = new Date()): Promise<SiteForecast | null> {
  const dayKey = londonWeatherDayKey(now);
  if (!dayKey) return null;
  return fetchForecastForCoords({
    latitude: CHICHESTER.latitude,
    longitude: CHICHESTER.longitude,
    label: CHICHESTER.label,
    dayKey,
  });
}

export async function fetchForecastForPostcode(
  postcode: string,
  label?: string,
  now = new Date(),
): Promise<SiteForecast | null> {
  const dayKey = londonWeatherDayKey(now);
  if (!dayKey) return null;
  const coords = await geocodePostcode(postcode);
  if (!coords) return null;
  return fetchForecastForCoords({
    ...coords,
    label: label || postcode,
    postcode,
    dayKey,
  });
}

export async function fetchJobSiteForecasts(
  targets: JobWeatherTarget[],
  now = new Date(),
): Promise<SiteForecast[]> {
  const rows = await Promise.all(targets.map(async (target) => {
    const suffix = target.labels.length ? target.labels.join(', ') : '';
    const label = suffix ? `${target.postcode} · ${suffix}` : target.postcode;
    return fetchForecastForPostcode(target.postcode, label, now);
  }));
  return rows.filter((row): row is SiteForecast => !!row);
}
